import React, { useState } from 'react';

import ContentLoader from 'react-content-loader';

import { useClientHasRendered } from '../../helpers/hooks';
import { AppDownloadModal } from '../AppDownloadModal/AppDownloadModal';
import { AppStoreButtons } from '../Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Button } from '../Clickables/Buttons/Button';
import { Counter } from '../Counter/Counter';
import { Heading } from '../Heading/Heading';
import { ChevronDownIcon } from '../Icons/ChevronDownIcon';
import NextImage from 'next/image';
import { QRCode } from '../QRCode/QRCode';
import { Text } from '../Text/Text';
import { useAnalytics } from '../../contexts/AnalyticsContext/AnalyticsContext';
import { trackEvent } from '../../helpers/analytics/trackEvent';

export interface IProductTableRow {
  productId: string;
  displayName: string;
  imageUrl: string;
  brandName: string;
  categoryName: string;
  numberOfCatches: number;
  position: number;
}

interface IProductTableProps {
  products: IProductTableRow[];
  isLoading?: boolean;
  loadingRows?: number;
  hasMoreRows: boolean;
  onSeeMoreClicked: () => void;
  appDownloadCTAHeadline: string;
  isPremium: boolean;
}

interface IRowProps {
  product: IProductTableRow;
  isFaded?: boolean;
}

const ROW_HEIGHT = 66;

const getProductTitle = (title: string): string => {
  if (title.includes('Natural baits ')) {
    /* eslint-disable no-magic-numbers */
    return title.slice(14);
  }
  return title;
};

const Row = ({
  product: { imageUrl, displayName, categoryName, numberOfCatches, brandName, position },
  isFaded = false,
}: IRowProps) => {
  const title = `${brandName} ${displayName}`;

  return (
    <div className="text-midnight col-span-12 mb-1.5 bg-white px-6 sm:mx-11 md:mx-4">
      <div className="grid grid-cols-12 py-3">
        <div className="text-clownfish col-span-1 col-start-1 row-start-1 flex items-center text-xl font-semibold">
          {position}
        </div>
        <div className="relative col-span-2 col-start-2 row-start-1 mx-auto flex size-full place-content-center">
          <NextImage
            className="m-auto max-h-12 object-contain px-2 lg:max-h-10"
            alt={`${brandName} ${displayName}`}
            src={imageUrl}
            fill
            unoptimized
          />
        </div>
        <div className="col-span-6 col-start-4 row-start-1 mr-2 flex flex-col place-content-center">
          <div className="text-trench truncate pb-2" title={getProductTitle(title)}>
            {getProductTitle(title)}
          </div>
          <div className="text-abyss text-xs">{categoryName}</div>
        </div>
        <div className="col-span-2 col-start-11 row-start-1 flex items-center">
          <Counter variant="sm">{numberOfCatches}</Counter>
        </div>
        {isFaded && (
          <div
            className="col-span-12 col-start-1 row-start-1"
            style={{
              height: 42,
              background:
                'linear-gradient(rgb(255, 255, 255, 0.8) 60%, rgba(255, 255, 255, 0) 100%)',
            }}
          />
        )}
      </div>
    </div>
  );
};

export const ProductTable = ({
  products,
  isLoading,
  loadingRows = 5,
  hasMoreRows,
  onSeeMoreClicked,
  appDownloadCTAHeadline,
  isPremium,
}: IProductTableProps) => {
  const clientHasRendered = useClientHasRendered();
  const { currentPage } = useAnalytics();
  const [isAppDownloadModalOpen, setIsAppDownloadModalOpen] = useState(false);

  const downloadButtonClick = (): void => {
    void trackEvent({
      name: 'mykiss_cta_button_clicked',
      properties: {
        source: 'ProductTable',
        type: 'downloadModal',
        page: currentPage,
      },
    });
    setIsAppDownloadModalOpen(true);
  };

  return (
    <div className="bg-lake-fog grid w-full grid-cols-12 pb-3">
      <div className="col-span-full grid grid-cols-12 px-6 sm:mx-11 md:mx-4">
        <div className="font-heading text-abyss col-span-3 py-3 text-left text-sm font-semibold md:py-4">
          Rating
        </div>
        <div className="font-heading text-abyss col-span-6 py-3 text-left text-sm font-semibold md:py-4">
          Bait name
        </div>
        <div className="font-heading text-abyss col-span-3 py-3 text-left text-sm font-semibold md:py-4">
          Catches
        </div>
      </div>
      {clientHasRendered && !isLoading ? (
        <>
          {!isPremium && products.length > 0 && (
            <div className="col-span-full flex flex-col items-center bg-white py-7 sm:mx-11 md:mx-4">
              <Heading
                variant="xs"
                className="mx-auto mb-6 w-2/3 text-center sm:mb-4 sm:w-1/2 md:mb-3"
              >
                {appDownloadCTAHeadline}
              </Heading>
              <Text>The best baits based on real anglers&apos; catches</Text>
              <Button onClick={downloadButtonClick} size="sm" className="hidden sm:flex">
                Download app
              </Button>
              <div className="flex w-full items-center justify-center sm:hidden">
                <AppStoreButtons orientation="vertical" source="ProductTableNoPro" />
              </div>
              <AppDownloadModal
                appDownloadSource="ProductTable"
                isModalOpen={isAppDownloadModalOpen}
                onCloseModal={() => setIsAppDownloadModalOpen(false)}
                modalHeader="Get the full experience in the Fishbrain app"
                modalText="Explore all Pro features including top baits, catch locations and depth maps."
                showTextOnMobile
              />
            </div>
          )}
          {products.length > 0 &&
            products.map((product, index) => (
              <Row
                key={`ProductTable-row-${product.position}-${product.productId}`}
                product={product}
                isFaded={!isPremium && index === 0}
              />
            ))}
          {products.length === 0 && (
            <div className="py-xs col-span-full flex flex-col items-center bg-white sm:mx-11 md:mx-4">
              <Heading
                variant="xs"
                className="mx-auto mb-6 w-2/3 text-center sm:mb-4 sm:w-1/2 md:mb-3"
              >
                Oops! There are no top baits registered here yet
              </Heading>
              <Text className="mx-auto mb-6 w-2/3 text-center sm:mb-4 sm:w-1/2 md:mb-3">
                Download the Fishbrain app to log your first catch and the baits you used.
              </Text>
              <div className="flex gap-4">
                <AppStoreButtons
                  orientation="vertical"
                  className="justify-center"
                  source="ProductTableNoCatches"
                />
                <QRCode className="hidden w-full sm:flex" source="ProductTableNoCatches" />
              </div>
            </div>
          )}
          {hasMoreRows && (
            <Button
              variant="textPrimary"
              className="col-span-full mx-auto my-4 font-semibold focus:outline-hidden"
              onClick={onSeeMoreClicked}
            >
              See more
              <ChevronDownIcon />
            </Button>
          )}
        </>
      ) : (
        <ContentLoader
          uniqueKey="product-table-loader-key"
          style={{ width: '100%', height: loadingRows * ROW_HEIGHT }}
        >
          <rect x="0" y="0" width="100%" height={loadingRows * ROW_HEIGHT} />
        </ContentLoader>
      )}
    </div>
  );
};
